/**
 * Search People API Contract (SCH-5038)
 *
 * GET /v1/users/search?q=<name or email>
 * Super-admin only. Finds School Journey accounts across every school by first name, last name, full name or email
 * (prefix, case-insensitive), so the share viewer's Share panel can grant an AI artifact to a person. Only accounts
 * with a usable email are returned; nobody is invited and no account is created.
 *
 * Source of truth: `apps/backend/src/api/users/(app-users)/v1/use-cases/search-people/schema.ts`
 * Consumer: `apps/share/src/lib/people-search.ts`
 */

import { z } from 'zod'

/** The shortest query the search answers, after trimming. */
export const SEARCH_PEOPLE_MIN_QUERY = 2
/** The longest query it takes. */
export const SEARCH_PEOPLE_MAX_QUERY = 100
/** How many people one search returns at most. */
export const SEARCH_PEOPLE_LIMIT = 8

export const SearchPeopleQuerySchema = z.object({
  q: z.string().trim().min(SEARCH_PEOPLE_MIN_QUERY).max(SEARCH_PEOPLE_MAX_QUERY)
})

export type SearchPeopleQuery = z.infer<typeof SearchPeopleQuerySchema>

/**
 * One account. `platformUserId` is `User.publicId`, the value Clerk holds as `external_id`, which the ops store's
 * grant matching compares with the viewer's JWT. `schools` are the names of the schools the person belongs to.
 */
export const PersonSearchResultSchema = z.object({
  platformUserId: z.string().min(1),
  name: z.string(),
  email: z.string().min(1),
  schools: z.array(z.string())
})

export type PersonSearchResult = z.infer<typeof PersonSearchResultSchema>

export const SearchPeopleResponseSchema = z.object({
  data: z.array(PersonSearchResultSchema).max(SEARCH_PEOPLE_LIMIT)
})

export type SearchPeopleResponse = z.infer<typeof SearchPeopleResponseSchema>
